// SinyalSatiri — Sinyal Listesi (CR2-05) timeline'ında tek satır.
// İki varyant: push (accent border-left, full opacity) ve low (dashed,
// opacity 0.55). status pill: yeni / incelendi / aksiyon / atlandı.
//
// onClick → /sinyal/:fund (deep-link). App state'i `signalStatuses[id]`
// güncellemesi navigation guard üzerinden değil, onClick callback'i ile yapılır.

import InlineAnomalyChip from "./InlineAnomalyChip";
import type { Anomaly, QueuedSignal, SignalStatus } from "../types/api";

interface Props {
  signal: QueuedSignal;
  status: SignalStatus;
  anomaliesForFund: Anomaly[];
  onClick: (signal: QueuedSignal) => void;
}

function kindBadgeColors(
  kind: QueuedSignal["kind"],
  isLow: boolean
): { bg: string; text: string } {
  // BUY → gain, PROFIT_TAKE/SELL → warn
  const isBuy = kind === "BUY";
  if (isLow) {
    return isBuy
      ? { bg: "bg-gain-500/10", text: "text-gain-400/80" }
      : { bg: "bg-warn-500/10", text: "text-warn-400/80" };
  }
  return isBuy
    ? { bg: "bg-gain-500/15", text: "text-gain-400" }
    : { bg: "bg-warn-500/15", text: "text-warn-400" };
}

function statusPillClass(s: SignalStatus, isLow: boolean): string {
  const base = isLow ? "text-[9px] px-1 py-0.5" : "text-[10px] px-1.5 py-0.5";
  switch (s) {
    case "yeni":
      return `${base} uppercase tracking-wider rounded ${
        isLow
          ? "bg-accent-500/15 text-accent-300/80"
          : "bg-accent-500/20 text-accent-300"
      } font-medium`;
    case "incelendi":
      return `${base} uppercase tracking-wider rounded bg-ink-700 text-ink-300 font-medium`;
    case "aksiyon":
      return `${base} uppercase tracking-wider rounded ${
        isLow
          ? "bg-gain-500/15 text-gain-400/80"
          : "bg-gain-500/20 text-gain-400"
      } font-medium`;
    case "atlandı":
      return `${base} uppercase tracking-wider rounded ${
        isLow
          ? "bg-loss-500/15 text-loss-400/80"
          : "bg-loss-500/20 text-loss-400"
      } font-medium`;
  }
}

export default function SinyalSatiri({
  signal,
  status,
  anomaliesForFund,
  onClick,
}: Props) {
  const isLow = signal.tier === "low";
  const { bg: kindBg, text: kindText } = kindBadgeColors(signal.kind, isLow);

  // Border-left türü — push: accent solid 3px / low: dashed 2px
  const containerClass = isLow
    ? "w-full text-left rounded-xl bg-ink-800/70 border border-ink-700/70 sinyal-low px-3 py-2 hover:bg-ink-700/40 transition"
    : "w-full text-left rounded-xl bg-ink-800 border border-ink-700 sinyal-push hover:border-accent-500/60 hover:bg-ink-700/50 transition px-3 py-2.5";

  return (
    <button type="button" onClick={() => onClick(signal)} className={containerClass}>
      <article>
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className={`fon-dili font-medium ${
              isLow ? "text-ink-200 text-[12px]" : "text-ink-100 text-[13px]"
            }`}
          >
            {signal.fund}
          </span>
          <span
            className={`uppercase tracking-wider font-medium rounded ${
              isLow ? "text-[9px] px-1 py-0.5" : "text-[10px] px-1.5 py-0.5"
            } ${kindBg} ${kindText}`}
          >
            {signal.kind}
          </span>
          <span
            className={`fon-dili ${
              isLow ? "text-[11px] text-ink-300" : "text-[12px] text-ink-200"
            }`}
          >
            {isLow ? signal.score : `skor ${signal.score}`}
          </span>

          {/* Low pill (sadece low tier) */}
          {isLow && (
            <span className="text-[9px] uppercase tracking-wider px-1 py-0.5 rounded bg-ink-700 text-ink-400">
              low
            </span>
          )}

          {/* Inline ⚠ chip — anomali olan fonlarda */}
          {!isLow && anomaliesForFund.length > 0 && (
            <InlineAnomalyChip
              fund={signal.fund}
              anomalies={anomaliesForFund}
              size={9}
            />
          )}

          {/* Status pill — sağa it */}
          <span className={`ml-auto ${statusPillClass(status, isLow)}`}>
            {status}
          </span>
        </div>

        <p
          className={`hedef-dili leading-snug ${
            isLow ? "text-ink-200 text-[13px] mt-1" : "text-ink-100 text-sm mt-1.5"
          }`}
        >
          {signal.summary}
        </p>
        <p
          className={`fon-dili leading-tight ${
            isLow ? "text-[10px] text-ink-500 mt-1" : "text-[11px] text-ink-400 mt-1.5"
          }`}
        >
          {signal.age_label}
        </p>
      </article>
    </button>
  );
}
